import { unref } from '@vue/composition-api';
import AlarmMsg from '@/utils/alarmMsg';

import { addEstimate as addEstimateAPI } from '@/api/ggoods/detail';
import { getPossibleNotLogin } from '@/api/main/main';
import { getPossible } from '@/api/ggoods/detail';
import { getOrderTempOrder } from '@/api/order/order';

import { useRouter, useRoute, useUserInfo } from '@/utils/hooks';
import * as OrderSessionManager from '@/utils/OrderSessionManager';
import _difference from 'lodash/difference';

import useAddCartAPI from '@/api/hooks/useAddCartAPI';
import useRemoveCartAPI from '@/api/hooks/useRemoveCartAPI';

import { tera } from '@/helpers/ad/tera/tera.js';
import {
	crmBizshopShortcutGoorder,
	crmBizshopShortcutAddtocart,
	crmBizshopShortcutEstimate,
	crmEstimateListDirectCheckoutClick,
} from '@/helpers/ad/crm/crm.js';

import { useStore } from '@vueblocks/vue-use-vuex';
import { rmRestrictionCheck } from '@/views/ggoods/GgoodsDetail/common';

export default function useGoodsUtils() {
	const router = useRouter();
	const route = useRoute();
	const store = useStore();

	const { withLoggedIn, isMasterMember } = useUserInfo();

	const mutationAddCart = useAddCartAPI();
	const mutationRemoveCart = useRemoveCartAPI();

	const showRMMessages = rmInfo => {
		// 물품 유형에 따라 구매 불가 체크
		if (rmInfo.userRmFlag === 'N') {
			AlarmMsg.showMsg(
				'고객님은 서비스 정책에 따라 해당 상품 유형을 구매하실 수 없습니다.<br>고객센터 1588-0108로 문의 바랍니다.'
			);
			return;
		}

		// 신규 가입 이벤트 구매 가능 여부
		if (rmInfo.newJoinGoodsYn === 'Y' && rmInfo.newJoinBuyPossible === 'N') {
			AlarmMsg.showMsg('신규가입자 대상 이벤트 상품입니다.</br>고객님은 구매가 불가능합니다.');
			return;
		}

		// 첫구매 여부 Check
		if (rmInfo.firstBuyFlag === 'Y' && rmInfo.firstBuyCheckFlag === 'N') {
			AlarmMsg.showMsg('고객님은 이벤트 조건에 부합하지 않아 이벤트에 참여하실 수 없습니다.');
			return;
		}
		// 상품 판매 시간이 유효한가?
		if (rmInfo.saleLimitDateCheckFlag === 'N') {
			AlarmMsg.showMsg('선택하신 상품의 판매 시간이 종료되었습니다.<br>판매 시간 확인 후 이용해주세요.');
			return;
		}

		// 판매 잔여 수량이 유효한가?
		if (rmInfo.rmCntCheckFlag === 'N') {
			AlarmMsg.showMsg('죄송합니다. 준비된 상품이 모두 소진되었습니다.<br>다른 상품을 이용해주세요.');
			return;
		}

		AlarmMsg.showMsg(
			'고객님은 서비스 정책에 따라 해당 상품 유형을 구매하실 수 없습니다.<br>고객센터 1588-0108로 문의 바랍니다.'
		);
	};

	/**
	 * 구매 불가능한 상품 목록 확인
	 * @param {string[]} goodsNoList 확인 할 상품 번호 목록
	 * @returns {object[]} 구매 불가능한 상품들의 구매제한정보 목록 - `상품정보`가 아님
	 * @throws {Error}
	 */
	const checkBlockedItems = async goodsNoList => {
		if (!Array.isArray(unref(goodsNoList))) return await checkBlockedItems([unref(goodsNoList)]);

		const response = await getPossible({ goodsNo: unref(goodsNoList) });

		// 판매중지된 상품 존재시
		if (response.code === 'SUC0000' && response.result?.resultList3?.susGoodsYn === 'Y') {
			await AlarmMsg.showMsg(response.result?.resultList3?.susGoodsNm + '은(는) 더 이상 구매하실 수 없는 상품입니다.');
			return { susGoodsYn: response.result?.resultList3?.susGoodsYn };
		}

		if (response.code === 'ERR0223') {
			AlarmMsg.showMsg('구매 가능한 상품이 없습니다.');
			return;
		}

		// 장바구니 > 견적서담기, 온라인견적서 > 담은상품 불러오기(possible 체크, updateEstimateGoodsInfo 체크 중복 호출 됨)
		if (response.code === 'ERR0504') {
			AlarmMsg.showMsg('유효기간이 다른 상품은 함께 구매하실 수 없습니다.');
			throw new Error(`[${response.code}]`);
		}

		if (response.code !== 'SUC0000') {
			throw new Error(`[${response.code}] ${response.message}`);
		}

		/** 상품별 구매제한 확인 결과 */
		const goodsList = response.result.resultList;
		const rmTightenCheck = response.result.resultList4;
		console.log(rmTightenCheck);

		const blockedItems = goodsList
			.map(item => {
				// 신규 가입 이벤트 구매 가능 여부
				if (item.newJoinGoodsYn === 'Y' && item.newJoinBuyPossible === 'N') {
					store.dispatch('setGoodsResponseData', { newJoinBuyPossible: 'N' });
					return item;
				}
				// 특정 상품 구매 제한
				if (item.userRmFlag === 'N') return item;
				// 첫구매 여부
				if (item.firstBuyFlag === 'Y' && item.firstBuyCheckFlag === 'N') return item;
				// 상품 판매시간
				if (item.saleLimitDateCheckFlag === 'N') return item;
				// 판매 잔여 수량
				if (item.rmCntCheckFlag === 'N') return item;
				// 온라인견적서, 장바구니 주문에서 RM적용
				if (goodsList.length > 1) {
					// ID당 구매가능 수량 (API상에서 해당 값이 내려오지 않고있음)
					if (item.rmIdBuyCntFlag === 'Y') return item;
					// 동일수신번호제한 (API상에서 해당 값이 내려오지 않고있음)
					if (item.rmRecvNumFlag === 'Y') return item;
					// 판매 중단 된 상품
					if (item.goodsStateCd === 'SUS') return item;
				}
				return;
			})
			.filter(Boolean);

		return { blockedItems, rmTightenCheck };
	};

	const includesBlockedItem = async goodsNoList => {
		if (!Array.isArray(unref(goodsNoList))) return await includesBlockedItem([unref(goodsNoList)]);
		const { blockedItems } = await checkBlockedItems(goodsNo);
		return blockedItems?.length > 1;
	};

	/**
	 * 비로그인 상태의 구매가능여부 확인
	 * @param {string} goodsNo
	 * @returns {Promise<boolean>}
	 */
	const isOrderableWithoutLoggedIn = async goodsNo => {
		try {
			const response = await getPossibleNotLogin({ goodsNo: [unref(goodsNo)] });

			if (response.code !== 'SUC0000') {
				console.log(`${response.code}: ${response.message}`);
				return false;
			}

			const checkInfo = response.result.resultList[0];

			// 상품 판매시간
			if (checkInfo.saleLimitDateCheckFlag === 'N') {
				AlarmMsg.showMsg('선택하신 상품의 판매 시간이 종료되었습니다.<br />판매 시간 확인 후 이용해주세요.', '안내');
				return false;
			}
			// 판매 잔여 수량
			if (checkInfo.rmCntCheckFlag === 'N') {
				AlarmMsg.showMsg('죄송합니다. 준비된 상품이 모두 소진되었습니다.<br />다른 상품을 이용해주세요.', '안내');
				return false;
			}
			return true;
		} catch (error) {
			console.error('[getPossibleNotLogin]', error);
		}
		return false;
	};

	/** 장바구니 담기 */
	const addCart = withLoggedIn(async (goodsNo, detail, isCrm, categorySeq) => {
		const goodsNoList = Array.isArray(unref(goodsNo)) ? unref(goodsNo) : [unref(goodsNo)];
		//
		// 장바구니 담기 시 RM 체크로직
		// 현재 AS-IS상에 관련 코드가 포함되어있으나 실제 적용하지는 않고 있음.
		//
		// try {
		//   const blockedItems = await checkBlockedItems(goodsNoList);

		//   if (unref(goodsNoList).length < 1) return;

		//   // 제한된 상품을 포함 할 경우
		//   if (blockedItems.length) {
		//     const blockedItemNames = blockedItems.map((item) => `[${item.brandName}] ${item.goodsName}`);

		//     // 모든 상품이 구매제한 된 경우 제한
		//     if (unref(goodsNoList).length === blockedItems.length) {
		//       await AlarmMsg.showMsg(`${blockedItemNames.join('<br />')}<br /> 은 장바구니에 담을 수 없습니다.`);
		//       return;
		//     }

		//     // 일부 상품이 구매제한 된 경우 confirm 대화창에 제한된 상품 목록을 보여주고 해당 상품을 제외한 상태에서 장바구니 담도록 안내
		//     if (
		//       await AlarmMsg.showSafetyConfirm(
		//         `${blockedItemNames.join('<br />')}<br /> 은 장바구니에 담을 수 없습니다.<br />나머지 상품을 장바구니에 담으시겠습니까?`,
		//       )
		//     ) {
		//       await mutationAddCart.mutate(
		//         _difference(
		//           unref(goodsNoList),
		//           blockedItems.map(({ goodsNo }) => goodsNo),
		//         ),
		//         {
		//           async onSuccess(data) {
		//             if (await AlarmMsg.showSafetyConfirm('장바구니에 담았습니다. 장바구니로 바로 이동하시겠습니까?')) {
		//               router.push({ name: 'cartSend' });
		//               return;
		//             }
		//           },
		//           async onError(error) {
		//             console.error('장바구니 담기 실패', error);
		//           },
		//         },
		//       );
		//     }
		//     return;
		//   }
		// } catch (error) {
		//   console.log(error);
		//   return;
		// }
		const param = { goodsNo: unref(goodsNoList), categorySeq };

		await mutationAddCart.mutate(param, {
			async onSuccess(data) {
				if (data.duplicateListCount > 0) {
					AlarmMsg.showMsg('이미 장바구니에 담은 상품입니다.');
				} else {
					/** TERA 제품 상세 - 장바구니 담기 */
					tera.detail.view(
						detail.goodsNo,
						detail.goodsName,
						detail.discountPrice,
						1,
						detail.categoryName?.split(',')[0] ? `'${detail.categoryName.split(',')[0]} > ${detail.brandName}'` : ''
					); //제품ID, 제품명, 제품단가, 제품수량, 카테고리-옵션
					tera.detail.cart([String(detail.goodsNo)]);

					/** CRM 장바구니 담기 숏컷 클릭 */
					if (isCrm) crmBizshopShortcutAddtocart(detail);

					if (await AlarmMsg.showSafetyConfirm('장바구니에 담았습니다. 장바구니로 바로 이동하시겠습니까?')) {
						router.push({ name: 'cartSend' });
						return;
					}
				}
			},
			async onError(error) {
				console.error('장바구니 담기 실패', error);
			},
		});
	});

	/** 장바구니 삭제 */
	const removeCart = withLoggedIn(async (cartSeqs, selectedAll, list, selectedGoodsNoList, callback) => {
		if (!(await AlarmMsg.showSafetyConfirm('선택하신 상품을 장바구니에서 삭제하시겠습니까?'))) return;

		await mutationRemoveCart.mutate(unref(cartSeqs), {
			async onSuccess() {
				/** TERA 장바구니 삭제*/
				tera.reset.cart();
				unref(list).map(item => {
					tera.cart.view(item.goodsNo, item.goodsName, item.discountPrice, 1, '');
				});

				if (selectedAll) {
					tera.cart.allDelete();
				} else {
					tera.cart.delete(selectedGoodsNoList.map(item => String(item)));
				}

				callback?.();
			},
			async onError(error) {
				console.error('장바구니 삭제 실패', error);
			},
		});
	});

	/**
	 * 바로구매
	 * @param {number|number[]} goodsNo
	 * @param {object} options
	 * @param {number[]} options.orderTempSeq 임시저장 id
	 * @param {number[]} options.cartIds 장바구니id(cartSeq) 주문 완료되면 장바구니에서 삭제 등의 용도로 사용
	 */
	const buyNow = withLoggedIn(
		async (
			goodsNo,
			{ orderTempSeq, cartIds, isEstimateView, goodsCategory } = {},
			detail,
			crmType,
			callback,
			estimateNm,
			customProc
		) => {
			const goodsNoList = Array.isArray(unref(goodsNo)) ? unref(goodsNo) : [unref(goodsNo)];

			// RM 적용 상품 리스트
			const { blockedItems, rmTightenCheck } = await checkBlockedItems(goodsNoList);
			try {
				if (unref(goodsNoList).length < 1) return;
				// 제한된 상품을 포함 할 경우
				if (blockedItems.length) {
					//온라인 견적서 신규이벤트 상품 체크
					if (customProc && customProc === '001') {
						let responseData = store.getters['goodsResponseData'];
						if (responseData && responseData.newJoinBuyPossible && responseData.newJoinBuyPossible === 'N') {
							await AlarmMsg.showMsg(
								'신규가입자 대상 이벤트 상품이<br/> 포함되어 있습니다. <br/>제외 후 진행해 주시기 바랍니다.'
							);
							store.dispatch('setGoodsResponseData', {});
							return;
						}
					}
					// 단건시 공통 메시지 처리
					if (unref(goodsNoList).length === 1) {
						showRMMessages(blockedItems[0]);
						return;
					}
					const blockedItemNames = blockedItems.map(item => `[${item.brandName}] ${item.goodsName}`);
					// 모든 상품이 구매제한 된 경우 제한
					if (unref(goodsNoList).length === blockedItems.length) {
						await AlarmMsg.showMsg(`${blockedItemNames.join('<br />')}<br /> 은(는) 함께 구매하실 수 없습니다.`);
						return;
					}
					// 일부 상품이 구매제한 된 경우 confirm 대화창에 제한된 상품 목록을 보여주고 해당 상품을 제외한 상태에서 구매 가능하도록 안내
					if (
						unref(isEstimateView)
							? await AlarmMsg.showSafetyConfirm(
									`${blockedItemNames.join(
										'<br />'
									)}<br /> 은(는) 함께 구매하실 수 없습니다.<br />나머지 상품을 바로구매(구매 시 고객님의 현재 등급 할인율 적용)하시겠습니까?`
							  )
							: await AlarmMsg.showSafetyConfirm(
									`${blockedItemNames.join(
										'<br />'
									)}<br /> 은(는) 함께 구매하실 수 없습니다.<br />나머지 상품을 바로구매하시겠습니까?`
							  )
					) {
						// 나머지 상품 goodsNo
						const passGoodsNoList = _difference(
							unref(goodsNoList),
							blockedItems.map(({ goodsNo }) => goodsNo)
						);
						const passGoodsCategoryList = unref(goodsCategory).filter(item => passGoodsNoList.includes(item.goodsNo));
						
						const sessionId = OrderSessionManager.createSession(passGoodsNoList, {
								orderTempSeq: unref(orderTempSeq),
								cartIds: unref(cartIds),
								goodsCategory: passGoodsCategoryList,
							}
						);

						const { rmTightenCheck: rmTightenCheck2 } = await checkBlockedItems(passGoodsNoList);

						/** CRM 바로주문 숏컷 클릭 */
						if (crmType === 'bizshop_shortcut_goorder') crmBizshopShortcutGoorder(unref(detail));
						if (crmType === 'estimate_list_direct_checkout_click')
							crmEstimateListDirectCheckoutClick(unref(detail), estimateNm);

						callback?.();

						// 사업자제한/환불계좌 체크
						// 사업자 제한일 경우
						if (!rmTightenCheck2.businessLimitationCheckStatus) {
							rmRestrictionCheck('B', unref(isMasterMember));
						}
						// 환불계좌 제한일 경우
						else if (!rmTightenCheck2.refundAccountCheckStatus) {
							rmRestrictionCheck('R', unref(isMasterMember));
						} else {
							router.push({
								name: 'gorderInfo',
								query: { sessionId },
							});
						}
					}
					return;
				}
			} catch (error) {
				console.log(error);
				return;
			}

			if (
				unref(isEstimateView)
					? await AlarmMsg.showSafetyConfirm(`구매 시 고객님의 현재 등급 할인율이 적용됩니다. 바로 구매 하시겠습니까?`)
					: true
			) {
				const sessionId = OrderSessionManager.createSession(unref(goodsNoList), {
					goodsCategory: unref(goodsCategory),
				});

				/** CRM 바로주문 숏컷 클릭 */
				if (crmType === 'bizshop_shortcut_goorder') crmBizshopShortcutGoorder(unref(detail));
				if (crmType === 'estimate_list_direct_checkout_click')
					crmEstimateListDirectCheckoutClick(unref(detail), estimateNm);

				callback?.();

				// 사업자제한/환불계좌 체크
				// 사업자 제한일 경우
				if (!rmTightenCheck.businessLimitationCheckStatus) {
					rmRestrictionCheck('B', unref(isMasterMember));
				}
				// 환불계좌 제한일 경우
				else if (!rmTightenCheck.refundAccountCheckStatus) {
					rmRestrictionCheck('R', unref(isMasterMember));
				} else {
					router.push({
						name: 'gorderInfo',
						query: { sessionId },
					});
				}
			}
		}
	);

	/**
	 * 임시저장 주문 이어서 하기
	 * @param {number} orderTempSeq 임시저장 주문 id
	 */
	const buyContinue = withLoggedIn(async (orderTempSeq, rmTightenCheck) => {
		const { code, result } = await getOrderTempOrder({ orderTempSeq: unref(orderTempSeq) });

		if (code !== 'SUC0000') {
			return;
		}

		if (!result.resultList?.length) {
			await AlarmMsg('상품 정보가 없습니다.');
			return;
		}

		const tempOrderData = result.resultList[0];
		const goodsNoList = tempOrderData.orderGoods.map(({ goodsNo }) => goodsNo);
		const sessionId = OrderSessionManager.createSession(unref(goodsNoList), { orderTempSeq: unref(orderTempSeq) });

		// 사업자제한/환불계좌 체크
		// 사업자 제한일 경우
		if (!rmTightenCheck.businessLimitationCheckStatus) {
			rmRestrictionCheck('B', unref(isMasterMember));
		}
		// 환불계좌 제한
		else if (!rmTightenCheck.refundAccountCheckStatus) {
			rmRestrictionCheck('R', unref(isMasterMember));
		} else {
			router.push({
				name: 'gorderInfo',
				query: { sessionId },
				params: { isDialogOpen: true },
			});
		}
	});

	/**
	 * 온라인 견적서 담기
	 * @param {object[]} goodsNoList {goodsNo, categorySeq}
	 */
	const addEstimate = withLoggedIn(async (goodsNoList, detail, isCrm, callback, onlyEvntCheck, customAlert) => {
		let evntCheck = onlyEvntCheck;
		if (!Array.isArray(unref(goodsNoList)))
			return await addEstimate([unref(goodsNoList)], detail, isCrm, callback, onlyEvntCheck);

		const response = await addEstimateAPI({
			goodsList: unref(goodsNoList).map(item => {
				item.goodsNo = String(item.goodsNo);
				return item;
			}),
		});

		if (response.code === 'SUC0000') {
			// 판매중지된 상품 존재시
			if (response.result?.susGoodsYn === 'Y') {
				await AlarmMsg.showMsg(response.result.susGoodsNm + '은(는) 더 이상 구매하실 수 없는 상품입니다.');
				store.dispatch('setGoodsResponseData', response.result);
				return;
			}

			if (response.result && response.result.newJoinBuyPossible && response.result.newJoinBuyPossible === 'N') {
				if (customAlert !== true) {
					await AlarmMsg.showMsg('신규가입자 대상 이벤트 상품입니다. 고객님은 구매가 불가능합니다.');
				}
				store.dispatch('setGoodsResponseData', response.result);
				return;
			}
		}

		if (response.code === 'ERR0504') {
			AlarmMsg.showMsg('유효기간이 다른 상품은 함께 견적 담을 수 없습니다.');
			return;
		}

		if (evntCheck) {
			return;
		}

		if (response.code === 'ERR0500') {
			await AlarmMsg.showMsg(
				'담을 수 있는 수량은 최대 50개입니다.<br>담은 상품 불러오기에서 담은 상품을 삭제 후 이용해주세요.'
			);
			return;
		}
		if (response.code === 'ERR0501') {
			await AlarmMsg.showConfirm(
				`
      이미 견적서에 담긴 상품입니다.<br>
      온라인견적서로 이동하시겠습니까?
    `,
				'알림'
			)
				.then(() => {
					router.push('/estimate/list');
					return;
				})
				.catch(() => {});
			return;
		}

		if (response.code !== 'SUC0000') {
			console.log(response.code);
			console.log(response.meesage);
			return;
		}

		/** CRM 견적 담기 숏컷 클릭 */
		if (isCrm) crmBizshopShortcutEstimate(detail);

		callback?.();

		await AlarmMsg.showConfirm(
			`
      견적담기 하였습니다.<br>
      온라인견적서로 이동하시겠습니까?
    `,
			'알림'
		)
			.then(() => {
				router.push('/estimate/list');
				return;
			})
			.catch(() => {});
	});

	return {
		isOrderableWithoutLoggedIn,
		checkBlockedItems,
		addCart,
		removeCart,
		buyNow,
		addEstimate,
		buyContinue,
	};
}
